import { AlertTriangle, CircleHelp, Plus, Sparkles } from "lucide-react";
import type { FC, ReactNode } from "react";

import type {
  ForeignNational,
  StatusHealth
} from "@/api/types/foreignNational";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { useUserProfiles } from "@/features/userProfiles/context/UserProfilesContext";

import { useFirmPortalState } from "../../../clientPortal/queries";
import { formatShortDate, initialsOf } from "../../header/clientHeaderModel";
import { portalHint, usePortalState } from "../../hooks/usePortalState";
import {
  ClientWarning,
  DismissedWarnings
} from "../../components/ClientWarning";
import {
  activeDismissal,
  useWarningDismissals
} from "../../hooks/useWarningDismissals";
import { useMinimizedWarnings } from "../../lib/minimizedWarnings";
import {
  alsoLapsed,
  identityLine,
  statusBanner,
  statusBannerCompact,
  statusBannerKey,
  statusChip,
  titledName,
  type Tone
} from "./identityModel";

const STATUS_TONE: Record<Tone, StatusTone> = {
  red: "danger",
  amber: "warn",
  emerald: "success",
  neutral: "neutral"
};

const Chip: FC<{ tone: Tone; children: ReactNode; testId?: string }> = ({
  tone,
  children,
  testId
}) => (
  <StatusBadge tone={STATUS_TONE[tone]} dot data-testid={testId}>
    {children}
  </StatusBadge>
);

const Bit: FC<{ label: string; children: ReactNode; onClick?: () => void }> = ({
  label,
  children,
  onClick
}) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className="text-sm text-fg-3">{label}</span>
    {onClick ? (
      <button
        type="button"
        onClick={onClick}
        className="truncate text-left text-base hover:underline"
      >
        {children}
      </button>
    ) : (
      <span className="truncate text-base">{children}</span>
    )}
  </div>
);

const Missing: FC = () => <span className="text-fg-4">—</span>;

const getNameForInitials = (c: ForeignNational): string =>
  [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Client";

type Props = {
  clientId: string;
  client: ForeignNational | null;
  health: StatusHealth | null | undefined;
  onOpenPersonal: () => void;
  onOpenPassports: () => void;
  /** Scroll to the Portal Access card. */
  onOpenPortal: () => void;
};

/** Who the client is, at the top of the Overview: name, origin, the numbers
 * every form asks for, and whether their status is current. */
export const ClientIdentityHeader: FC<Props> = ({
  clientId,
  client,
  health,
  onOpenPersonal,
  onOpenPassports,
  onOpenPortal
}) => {
  const { get: getProfile } = useUserProfiles();
  const portalUpdatedAt = useFirmPortalState(clientId).dataUpdatedAt;
  const portal = usePortalState(
    clientId,
    Boolean(client?.email),
    portalUpdatedAt
  );

  if (!client) {
    return (
      <div
        className="flex items-start gap-3.5"
        data-testid="client-identity-loading"
      >
        <Skeleton className="size-15 rounded-lg" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
      </div>
    );
  }

  const name = titledName(client);
  const line = identityLine(client);
  const chip = statusChip(client.current_immigration_status, health);
  const avatarUrl = getProfile(clientId)?.avatar_data_url ?? undefined;
  const passportExp = formatShortDate(client.passport_expiration ?? null);
  const contact = client.phone?.trim() || client.email?.trim() || "";

  return (
    <div
      className="flex items-start gap-3.5 text-left"
      data-testid="client-identity"
    >
      <Avatar className="size-12">
        {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
        <AvatarFallback className="text-md text-fg-3">
          {initialsOf(getNameForInitials(client))}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1
            className="text-xl font-medium"
            data-testid="client-identity-name"
          >
            {name}
          </h1>
          {client.native_name?.trim() ? (
            <span className="text-md text-muted-foreground" lang="und">
              {client.native_name.trim()}
            </span>
          ) : null}
          {chip ? (
            <Chip tone={chip.tone} testId="client-identity-status">
              {chip.label}
            </Chip>
          ) : null}
          {portal === "active" ? (
            <button
              type="button"
              onClick={onOpenPortal}
              title={portalHint(portal) ?? undefined}
            >
              <Chip tone="emerald">Portal Active</Chip>
            </button>
          ) : portal === "off" || portal === "none" ? (
            <button
              type="button"
              onClick={onOpenPortal}
              title={portalHint(portal) ?? undefined}
            >
              <Chip tone="neutral">Portal Off</Chip>
            </button>
          ) : null}
        </div>
        {line ? (
          <p
            className="text-base text-muted-foreground"
            data-testid="client-identity-line"
          >
            {line}
          </p>
        ) : (
          <button
            type="button"
            onClick={onOpenPersonal}
            className="w-fit text-base text-muted-foreground hover:underline"
          >
            Add date of birth, citizenship and language
          </button>
        )}
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
          <Bit label="A-Number" onClick={onOpenPersonal}>
            {client.a_number ? (
              <span className="font-mono">{client.a_number}</span>
            ) : (
              <Missing />
            )}
          </Bit>
          <Bit label="Passport" onClick={onOpenPassports}>
            {client.passport_number || passportExp ? (
              <>
                {client.passport_number ? (
                  <span className="font-mono">{client.passport_number}</span>
                ) : null}
                {passportExp ? ` · exp ${passportExp}` : null}
              </>
            ) : (
              <Missing />
            )}
          </Bit>
          <Bit label="Status Ends" onClick={onOpenPersonal}>
            {client.duration_of_status ? (
              "D/S"
            ) : client.immigration_status_expiration ? (
              formatShortDate(client.immigration_status_expiration)
            ) : (
              <Missing />
            )}
          </Bit>
          <Bit label="Contact">{contact || <Missing />}</Bit>
        </div>
      </div>
    </div>
  );
};

type BannerProps = {
  clientId: string;
  firmId?: string | null;
  health: StatusHealth | null | undefined;
  firstName: string | null;
  status?: string | null;
  onAddStatus: () => void;
  onAutofill: () => void;
  onAsk: (prompt: string) => void;
};

/** Says plainly when the client's own status has ended or is about to.
 * Each user can minimize it; anyone on the team can dismiss it for the firm,
 * and it comes back when the status, its end date or the other lapsed items
 * change. */
export const StatusBanner: FC<BannerProps> = ({
  clientId,
  firmId,
  health,
  firstName,
  status,
  onAddStatus,
  onAutofill,
  onAsk
}) => {
  const { isMinimized, minimize, expand } = useMinimizedWarnings(firmId);
  const { dismissals, dismiss, restore } = useWarningDismissals(clientId);
  const copy = statusBanner(health, firstName, status);
  if (!copy) return null;
  const warning = health?.warning ?? null;
  const dismissed = activeDismissal(dismissals, warning);
  if (dismissed)
    return (
      <DismissedWarnings
        dismissals={[dismissed]}
        onRestore={(kind) => void restore(kind)}
        testId="status-banner-dismissed"
      />
    );
  const legacyId = statusBannerKey(clientId, health, status);
  const minimizeId = warning
    ? `${clientId}|${warning.kind}|${warning.fingerprint}`
    : legacyId;
  const lapsed = alsoLapsed(health);
  return (
    <ClientWarning
      tone={copy.tone}
      icon={copy.tone === "neutral" ? CircleHelp : AlertTriangle}
      title={copy.title}
      body={copy.body}
      footnote={
        lapsed.length
          ? `Also lapsed: ${lapsed
              .map((i) => `${i.label} (${formatShortDate(i.due)})`)
              .join(", ")}`
          : null
      }
      compactLabel={statusBannerCompact(health)}
      minimized={isMinimized(minimizeId, legacyId)}
      onMinimize={() => minimize(minimizeId)}
      onExpand={() => expand(minimizeId, legacyId)}
      onDismiss={
        warning ? (reason) => void dismiss(warning, reason) : undefined
      }
      testId={`status-banner-${copy.tone}`}
      actions={
        <>
          {copy.tone === "neutral" ? (
            <Button variant="secondary" size="xs" onClick={onAutofill}>
              <Sparkles /> Autofill From Documents
            </Button>
          ) : null}
          <Button variant="secondary" size="xs" onClick={onAddStatus}>
            <Plus /> Add Status Record
          </Button>
          <Button variant="ghost" size="xs" onClick={() => onAsk(copy.prompt)}>
            Ask Casey
          </Button>
        </>
      }
    />
  );
};
